import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { User, Baby, Users, Heart, Shield, Building, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, pricing } from '../data/mock';

const iconMap = {
  User: User,
  Baby: Baby,
  Users: Users,
  Heart: Heart,
  Shield: Shield,
  Building: Building,
};

const ServicesPage = () => {
  const [searchParams] = useSearchParams();
  const highlightedService = searchParams.get('service');
  const [hoveredServiceId, setHoveredServiceId] = useState(null);

  useEffect(() => {
    if (!highlightedService) return;
    const target = document.getElementById(`service-${highlightedService}`);
    if (!target) return;
    const timer = window.setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [highlightedService]);

  return (
    <div>
      {/* Page Header */}
      <section className="bg-teal-700 py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="text-center text-white">
            <p className="text-sand-200 font-medium tracking-[0.3em] uppercase text-xs mb-2">Услуги</p>
            <h1 className="text-4xl md:text-5xl font-serif">Направления работы</h1>
            <p className="mt-4 text-sand-100 max-w-2xl mx-auto">
              Профессиональная психологическая помощь для детей, взрослых и семей
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              const isHighlighted =
                highlightedService === service.serviceValue &&
                hoveredServiceId === null;
              
              return (
                <div
                  key={service.id}
                  id={`service-${service.serviceValue}`}
                  onMouseEnter={() => setHoveredServiceId(service.id)}
                  onMouseLeave={() => setHoveredServiceId(null)}
                  className={`group rounded-2xl p-8 transition-all duration-300 ${
                    isHighlighted
                      ? 'bg-teal-600 ring-2 ring-teal-300/80 shadow-lg'
                      : 'bg-sand-100 hover:bg-teal-600'
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-colors ${
                    isHighlighted ? 'bg-white/20' : 'bg-teal-100 group-hover:bg-white/20'
                  }`}>
                    <IconComponent 
                      size={32} 
                      className={`${isHighlighted ? 'text-white' : 'text-teal-600 group-hover:text-white'} transition-colors`} 
                    />
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl font-semibold mb-3 transition-colors ${
                    isHighlighted ? 'text-white' : 'text-stone-800 group-hover:text-white'
                  }`}>
                    {service.title}
                  </h3>
                  <p className={`leading-relaxed mb-4 transition-colors ${
                    isHighlighted ? 'text-white/80' : 'text-stone-600 group-hover:text-white/80'
                  }`}>
                    {service.description}
                  </p>

                  {/* Details */}
                  <ul className="space-y-2 mb-6">
                    {service.details.map((detail, idx) => (
                      <li key={idx} className={`flex items-center gap-2 text-sm transition-colors ${
                        isHighlighted ? 'text-white/80' : 'text-stone-600 group-hover:text-white/80'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          isHighlighted ? 'bg-white' : 'bg-teal-500 group-hover:bg-white'
                        }`} />
                        {detail}
                      </li>
                    ))}
                  </ul>

                  <Link to={`/appointment?service=${encodeURIComponent(service.serviceValue || '')}`}>
                    <Button
                      variant="outline"
                      className={`transition-colors rounded-full px-6 ${
                        isHighlighted
                          ? 'border-white text-white bg-white/10'
                          : 'border-teal-600 text-teal-600 group-hover:border-white group-hover:text-white group-hover:bg-white/10'
                      }`}
                    >
                      Записаться
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 md:py-20 bg-sand-50 section-contrast">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-teal-600 font-medium tracking-[0.3em] uppercase text-xs mb-2">
              Стоимость
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-800 mb-4">
              Тарифы на консультации
            </h2>
            <p className="text-stone-600 text-lg">
            Все консультации проводятся онлайн: FaceTime Video / VK Видео.
            <br />
            Оплата после консультации: перевод Revolut/PayPal/Тинькофф
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricing.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-2xl p-6 transition-all ${
                  plan.popular
                    ? 'md:order-2 bg-teal-600 text-white shadow-xl md:scale-105'
                    : 'md:order-1 bg-white border border-sand-200'
                } ${plan.id === 1 ? 'order-1 lg:order-1' : ''} ${plan.id === 3 ? 'order-2' : ''} ${
                  plan.id === 2 ? 'order-3 lg:order-3' : ''
                }`}
              >
                <h3 className={`text-lg font-semibold mb-2 ${plan.popular ? 'text-white' : 'text-stone-800'}`}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className={`text-3xl font-serif font-bold ${plan.popular ? 'text-white' : 'text-stone-800'}`}>
                    {plan.price}
                  </span>
                </div>
                <p className={`text-sm mb-4 ${plan.popular ? 'text-teal-100' : 'text-stone-500'}`}>
                  {plan.duration}
                </p>
                <Link
                  to={`/appointment?service=${encodeURIComponent(
                    plan.serviceValue || ''
                  )}`}
                >
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? 'bg-white text-teal-600 hover:bg-teal-50'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    Записаться
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
